/**
 * The web app's side of landing (packages/shared navigation/landing): it
 * gathers the facts, asks the shared rules where to go, and carries the answer
 * out as a view. The rules themselves live in shared, so web and mobile land
 * the same way.
 *
 * - After signing in (no screen named in the address), a shop with the
 *   Customers module opens on its customers instead of the dashboard.
 * - Opening a location from the executive dashboard does the same.
 *
 * Everything else keeps the dashboard view, which already picks executive vs
 * location by the same gates.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  accountFeaturesQueryKey,
  CUSTOMERS_FEATURE,
  getAccountFeatures,
  resolveLanding,
  type LandingDecision,
  type LandingFacts,
} from '@turn-connect/shared';
import { canReadCustomers } from '../customers/customersAccess';
import { useCustomersOn } from '../customers/customersQueries';
import { navigateToCustomers } from '../customers/customersNavigation';
import { navigateToLocationDashboard } from '../../utils/navigation';

export interface LandingGates {
  canSeeExecutive: boolean;
  canViewLocationDashboard: boolean;
  permissions: readonly string[];
}

const factsFor = (g: LandingGates, hasLocation: boolean, customers: { on: boolean; loaded: boolean }): LandingFacts => {
  const canRead = canReadCustomers(g.permissions);
  return {
    canSeeExecutive: g.canSeeExecutive,
    canViewLocationDashboard: g.canViewLocationDashboard,
    canViewWorkOrders: g.permissions.includes('workorders:read'),
    hasLocation,
    customers: { on: customers.on, loaded: customers.loaded || !canRead, canRead },
  };
};

/**
 * Lands a fresh sign-in. `begin()` marks one as pending (the app opened with
 * no view in the address); it is carried out once the account, its store and
 * its Customers answer are known, and dropped if the person moves on first.
 */
export function useSignInLanding(args: {
  gates: LandingGates;
  accountId: string | null | undefined;
  locationId: string | null | undefined;
  /** The account's stores have arrived (or the person has no account-level list). */
  locationSettled: boolean;
  /** The view on screen; a landing only replaces the default dashboard. */
  currentView: string;
}): { begin: () => void } {
  const { gates, accountId, locationId, locationSettled, currentView } = args;
  const pending = useRef(false);
  // Starting a landing re-runs the check: everything may already be known.
  const [started, setStarted] = useState(0);
  const account = accountId && accountId !== 'ALL' ? accountId : null;
  const customers = useCustomersOn(account, { enabled: canReadCustomers(gates.permissions) });

  useEffect(() => {
    if (!pending.current) return;
    if (currentView !== 'dashboard') {
      // Somewhere else was asked for (a link, a click): that wins.
      pending.current = false;
      return;
    }
    if (!locationSettled) return;
    const decision = resolveLanding({ kind: 'signIn' }, factsFor(gates, !!locationId, customers));
    if (decision.status === 'wait') return;
    pending.current = false;
    if (decision.destination === 'customers') navigateToCustomers({ accountId: account, locationId, replace: true });
  }, [gates, account, locationId, locationSettled, currentView, customers, started]);

  const begin = useCallback(() => {
    pending.current = true;
    setStarted((n) => n + 1);
  }, []);
  return { begin };
}

/**
 * Opens a location from the executive dashboard: its customers in a
 * customers-first account, else its dashboard. Reads the account's modules
 * through the shared cache (one GET per account, shared with every other
 * check), so a click costs nothing extra once the account has been seen.
 */
export function useOpenLocationLanding(gates: LandingGates): (accountId: string | undefined, locationId: string) => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    async (accountId, locationId) => {
      let customers = { on: false, loaded: true };
      if (accountId && canReadCustomers(gates.permissions)) {
        try {
          // Loaded on use: the dashboard (and its tests) need no API client to render.
          const { webApiClient } = await import('../../services/apiClient');
          const features = await queryClient.fetchQuery({
            queryKey: accountFeaturesQueryKey(accountId),
            queryFn: () => getAccountFeatures(webApiClient, accountId),
            staleTime: 60_000,
          });
          customers = { on: features.includes(CUSTOMERS_FEATURE), loaded: true };
        } catch {
          // An optional module's read must never stop a location opening.
        }
      }
      const decision: LandingDecision = resolveLanding({ kind: 'openLocation' }, factsFor(gates, true, customers));
      if (decision.status === 'go' && decision.destination === 'customers') navigateToCustomers({ accountId, locationId });
      else navigateToLocationDashboard(accountId, locationId);
    },
    [gates, queryClient]
  );
}
